import type { FC } from 'react'

import { SelectableCard, Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'

import { Translation } from '@/app/translations/Translation'
import { commonQueries } from '@/resources/queries'
import type { SubscriptionDepartment } from '@/resources/subscriptions'
import { commonSubscriptionResources } from '@/resources/subscriptions'

import { useNewSubscriptionContext } from './NewSubscriptionForm.context'

function filterUniqueCompanies(companies: SubscriptionDepartment[]) {
  return uniqBy(companies, company => `${company.customerId}-${company.customerName}`)
}

export const NewSubscriptionCompanies: FC = () => {
  const { data: setup, isLoading } = useQuery(
    commonQueries.subscriptionSetup(),
    commonSubscriptionResources.getSetup
  )
  const [companyId, setCompanyId] = useNewSubscriptionContext().companyId
  const [, setAllowPrivateNumber] = useNewSubscriptionContext().allowPrivateNumber
  const [, setAllowSubscriptions] = useNewSubscriptionContext().allowSubscriptions
  const [, setAllowEsim] = useNewSubscriptionContext().allowEsim

  if (isLoading) return <CompaniesSkeleton />
  if (!setup) throw Error('We encountered an error while loading companies.')

  const companyOptions = filterUniqueCompanies(setup.departments)

  return (
    <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {companyOptions.map(department => {
        const isSelected = companyId === department.customerId

        return (
          <div key={department.customerId} className="m-2">
            <SelectableCard
              bordered
              textLeft
              padding={16}
              colorMode="yellow"
              isSelected={isSelected}
              onClick={() => {
                if (isSelected) {
                  setCompanyId(null)
                  setAllowPrivateNumber(false)
                  setAllowSubscriptions(false)
                  setAllowEsim(false)
                  return
                }
                setCompanyId(department.customerId)
                setAllowPrivateNumber(Boolean(department.allowPrivateNumber))
                setAllowSubscriptions(Boolean(department.allowSubscriptions))
                setAllowEsim(Boolean(department.allowEsim))
              }}
            >
              <h3 className="mb-4 mt-1.5 text-2xl leading-none">{department.customerName}</h3>

              <p className="m-0 text-sm">
                <Translation id="Org. number" />{' '}
                <em className="mt-1 block text-xl not-italic leading-none">{department.orgNo}</em>
              </p>

              <div className="checkmark absolute right-2 top-2">
                {isSelected ? (
                  <CheckboxMarkedCircleOutlineIcon className="h-8 w-8" />
                ) : (
                  <CheckboxBlankCircleOutlineIcon className="h-8 w-8" />
                )}
              </div>
            </SelectableCard>
          </div>
        )
      })}
    </div>
  )
}

const CompaniesSkeleton: FC = () => {
  const tenElements = new Array(10)
  tenElements.fill(null)

  return (
    <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {tenElements.map((_, index) => (
        <div
          key={index}
          className="pointer-events-none m-2 rounded-md border border-transparent bg-transparent p-0"
        >
          <Skeleton height="126px" />
        </div>
      ))}
    </div>
  )
}
